import React, { useEffect, useState } from "react";
import { Dialog, DialogPanel } from "@headlessui/react";
import { Menu as MenuIcon, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Button from "../components/Button";
import LanguageSwitcher from "../components/LanguageSwitcher";
import Wordmark from "../components/Wordmark";
import { SECTION_IDS } from "../landingConfig";

/**
 * Sticky landing header.
 *
 * Transparent while the hero is under it, then solid white with a single
 * `shadow-sm` once the page has scrolled past 40px.
 *
 * The scroll listener works because the Landing page puts `landing-active` on
 * <html>, which releases `#root` from `height: 100vh` and lets the document
 * scroll (see index.css). Inside the app, `#root` is the scroll container and
 * `window.scrollY` would stay at 0.
 */
export default function Header() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll(); // account for a restored scroll position on load
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { href: `#${SECTION_IDS.features}`, label: t("landing.nav.features") },
    { href: `#${SECTION_IDS.how}`, label: t("landing.nav.how") },
    { href: `#${SECTION_IDS.results}`, label: t("landing.nav.results") },
    { href: `#${SECTION_IDS.audiences}`, label: t("landing.nav.audiences") },
  ];

  return (
    <header
      className={`sticky top-0 z-40 transition-colors duration-150 ease-out ${
        scrolled ? "border-b border-line bg-white shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8">
        <a
          href="#top"
          aria-label={t("landing.nav.home")}
          className="cursor-pointer rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        >
          <Wordmark />
        </a>

        <nav aria-label={t("landing.nav.features")} className="hidden lg:block">
          <ul className="flex items-center gap-1">
            {navItems.map((item) => (
              <li key={item.href}>
                <a
                  href={item.href}
                  className="inline-flex min-h-11 cursor-pointer items-center rounded-lg px-3 text-base text-muted transition-colors duration-150 ease-out hover:bg-gray-landing hover:text-black-landing focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          <LanguageSwitcher />
          {/* Subordinate to the single primary CTA. */}
          <Link
            to="/login"
            className="inline-flex min-h-11 cursor-pointer items-center rounded-lg px-3 text-base font-medium text-black-landing transition-colors duration-150 ease-out hover:bg-gray-landing focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            {t("landing.nav.login")}
          </Link>
          <Button href={`#${SECTION_IDS.contact}`}>{t("landing.nav.demo")}</Button>
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          aria-label={t("landing.nav.open_menu")}
          className="inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-lg text-black-landing transition-colors duration-150 ease-out hover:bg-gray-landing focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 lg:hidden"
        >
          <MenuIcon className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>

      {/*
        Headless UI Dialog gives us the focus trap, Esc-to-close and focus
        restoration to the trigger for free.
      */}
      <Dialog open={menuOpen} onClose={setMenuOpen} className="lg:hidden">
        <div className="fixed inset-0 z-50 bg-black/25" aria-hidden="true" />
        <DialogPanel className="fixed inset-0 z-50 flex flex-col overflow-y-auto bg-white px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <Wordmark />
            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              aria-label={t("landing.nav.close_menu")}
              className="inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-lg text-black-landing transition-colors duration-150 ease-out hover:bg-gray-landing focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              <X className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>

          <nav className="mt-6 flex-1">
            <ul className="flex flex-col gap-1">
              {navItems.map((item) => (
                <li key={item.href}>
                  <a
                    href={item.href}
                    onClick={() => setMenuOpen(false)}
                    className="flex min-h-11 cursor-pointer items-center rounded-lg px-3 text-lg text-black-landing transition-colors duration-150 ease-out hover:bg-gray-landing focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="mt-6 flex flex-col gap-4 border-t border-line pt-6">
            <LanguageSwitcher variant="inline" />
            <Button
              href={`#${SECTION_IDS.contact}`}
              onClick={() => setMenuOpen(false)}
            >
              {t("landing.nav.demo")}
            </Button>
            <Link
              to="/login"
              className="inline-flex min-h-11 cursor-pointer items-center justify-center rounded-lg border border-line px-5 text-base font-medium text-black-landing transition-colors duration-150 ease-out hover:bg-gray-landing focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              {t("landing.nav.login")}
            </Link>
          </div>
        </DialogPanel>
      </Dialog>
    </header>
  );
}
